'use client';

import {
  getClientApplicationConfig,
  getClientApplicationContext,
} from '@hc-frontend/core-context';
import { onlyDigits } from '@hc-frontend/core-utils-format';
import type { CompleteTrackingRepository } from '@hc-frontend/features-tracking';
import { fetchRealPhoneValidation } from '@hc-frontend/services';
import { trackExperimentView } from '@hc-frontend/third-party-snowplow';
import { createContext, ReactNode, useContext } from 'react';
import { ZodSchema } from 'zod';

const INVALID_PHONE_STATUSES = [
  'disconnected',
  'disconnected-70',
  'invalid-phone',
  'unreachable',
];

const VALIDATION_TIMEOUT = 3000;

type ExperimentDefinition = {
  experiment: string;
  treatment?: string;
};

const PhoneValidationRefinementExperimentContext =
  createContext<ExperimentDefinition>({
    experiment: '',
    treatment: '',
  });

export const usePhoneValidationRefinementExperiment = () =>
  useContext(PhoneValidationRefinementExperimentContext);

export function PhoneValidationRefinementExperimentProvider({
  children,
  experiment,
  treatment,
}: ExperimentDefinition & { children: ReactNode }): ReactNode {
  return (
    <PhoneValidationRefinementExperimentContext.Provider
      value={{ experiment, treatment }}
    >
      {children}
    </PhoneValidationRefinementExperimentContext.Provider>
  );
}

export type buildPhoneValidationRefinementParams = ExperimentDefinition & {
  trackingContext: CompleteTrackingRepository;
  baseValidation: ZodSchema;
};

export function buildPhoneValidationRefinement({
  experiment,
  treatment,
  trackingContext,
  baseValidation,
}: buildPhoneValidationRefinementParams) {
  const config = getClientApplicationConfig();
  const { log } = getClientApplicationContext(buildPhoneValidationRefinement);

  return baseValidation.refine(
    async (value) => {
      try {
        // Do not run refinement if base validation fails or if the value is empty.
        if (!baseValidation.safeParse(value).success || !value) {
          return true;
        }

        if (experiment && treatment) {
          trackExperimentView({
            experimentName: experiment,
            variantName: treatment,
            splitSource: 'amplitude',
          });
        }

        if (treatment !== 'variant') {
          return true;
        }

        const validation = await Promise.race([
          fetchRealPhoneValidation(config.healthcareApi.baseUrl, {
            phone: onlyDigits(value),
            domainUserId: trackingContext.tracker.getDomainUserId(),
            domainSessionId: trackingContext.tracker.getDomainUserInfo()[6],
          }),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('fetchRealPhoneValidation timeout')),
              VALIDATION_TIMEOUT,
            ),
          ),
        ]);

        if (validation.result !== 'success' || validation.body === undefined) {
          return true;
        }

        if (INVALID_PHONE_STATUSES.includes(validation.body.status)) {
          return false;
        }

        return true;
      } catch (e) {
        log({
          context: 'client',
          type: 'error',
          message:
            e instanceof Error
              ? e.message
              : 'fetchRealPhoneValidation unknown error',
        });

        return true;
      }
    },
    {
      message: 'Please enter an active phone number.',
    },
  );
}
